import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Tag, Newspaper, Baby, MapPin,
  Users, CalendarClock, ShieldCheck, BarChart2, CreditCard,
  Globe, Bell, BookOpen, Layers, ClipboardList, Briefcase,
  FolderOpen, HelpCircle, ScrollText, type LucideProps,
} from 'lucide-react';
import { useAuth } from '../../providers/auth-context';
import { useContainer } from '../../providers/container-context';
import { useAsync } from '../../hooks/use-async';
import { ConfirmModal } from '../ui';
import { NAV_GROUPS, PAGE_HEADINGS } from '../../router/navigation';

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super-admin',
  ADMIN: 'Admin',
  EDITOR: 'Éditeur',
  READER: 'Lecteur',
};

const ICON_MAP: Record<string, React.ComponentType<LucideProps>> = {
  LayoutDashboard, Building2, Tag, Newspaper, Baby, MapPin,
  Users, CalendarClock, ShieldCheck, BarChart2, CreditCard,
  Globe, Bell, BookOpen, Layers, ClipboardList, Briefcase,
  FolderOpen, HelpCircle, ScrollText,
};

function NavIcon({ name, size = 16 }: { name: string; size?: number }): JSX.Element {
  const Icon = ICON_MAP[name];
  if (!Icon) return <span style={{ width: size, height: size, display: 'inline-block' }} />;
  return <Icon size={size} strokeWidth={1.75} />;
}

export function AppShell(): JSX.Element {
  const { admin, isSuperAdmin, signOut } = useAuth();
  const container = useContainer();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const snapshot = useAsync(() => container.dashboard.snapshot.execute(), [container]);
  const badges = snapshot.data?.badges ?? { companies: 0, offers: 0, reviews: 0 };

  const [title, subtitle] = PAGE_HEADINGS[location.pathname] ?? ['BabiMams Admin', ''];

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleSignOut = useCallback(() => void signOut(), [signOut]);
  const roleLabel = admin?.role ? (ROLE_LABELS[admin.role] ?? admin.role) : '—';

  return (
    <div className="bbm-shell">

      {mobileOpen ? (
        <button
          type="button"
          className="bbm-sidebar-backdrop"
          aria-label="Fermer le menu"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <nav
        className={`bbm-sidebar${mobileOpen ? ' bbm-sidebar--open' : ''}`}
        style={{ width: collapsed ? 72 : 236 }}
        aria-label="Navigation principale"
      >
        {/* ── Header ── */}
        <div
          className="bbm-sidebar__head"
          style={collapsed ? { justifyContent: 'center', padding: '20px 10px' } : undefined}
        >
          {!collapsed ? (
            <img
              src="/logo.png"
              alt="BabiMams"
              aria-hidden="true"
              style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, objectFit: 'cover' }}
            />
          ) : null}
          {!collapsed ? (
            <span style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
              BabiMams
            </span>
          ) : null}
          <button
            type="button"
            className="bbm-sidebar__collapse-btn"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? 'Déployer le menu' : 'Réduire le menu'}
            style={{ marginLeft: collapsed ? 0 : 'auto' }}
          >
            {collapsed ? '▶' : '◀'}
          </button>
        </div>

        {/* ── Nav ── */}
        <div className="bbm-sidebar__nav">
          {NAV_GROUPS.map((group, groupIndex) => {
            const items = group.items.filter((item) => !item.superAdminOnly || isSuperAdmin);
            if (items.length === 0) return null;
            return (
              <div key={group.label ?? 'root'} style={{ marginTop: groupIndex > 0 ? 8 : 0 }}>
                {group.label && !collapsed ? (
                  <div className="bbm-sidebar__group" style={{ marginTop: groupIndex > 1 ? 6 : 0 }}>
                    {group.label}
                  </div>
                ) : null}
                {items.map((item) => {
                  const badge = item.badgeKey ? badges[item.badgeKey] : 0;
                  return (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      end={item.path === '/'}
                      title={item.label}
                      className={({ isActive }) =>
                        ['bbm-nav-item', isActive ? 'bbm-nav-item--active' : '', collapsed ? 'bbm-nav-item--collapsed' : '']
                          .filter(Boolean).join(' ')
                      }
                    >
                      <NavIcon name={item.icon} size={16} />
                      {!collapsed ? <span>{item.label}</span> : null}
                      {badge > 0 ? (
                        <span
                          style={{
                            ...(collapsed ? { position: 'absolute', top: 5, right: 5 } : { marginLeft: 'auto' }),
                            background: '#D67F9E',
                            color: '#fff',
                            fontSize: 10.5,
                            fontWeight: 800,
                            borderRadius: 99,
                            padding: '2px 7px',
                            lineHeight: 1.4,
                          }}
                        >
                          {badge}
                        </span>
                      ) : null}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div className="bbm-sidebar__foot">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, overflow: 'hidden' }}>
            <div
              title={admin?.name ?? roleLabel}
              style={{
                width: 32, height: 32, borderRadius: 99,
                background: 'rgba(127,179,213,0.2)',
                border: '1.5px solid rgba(127,179,213,0.35)',
                color: '#7FB3D5',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 12, flexShrink: 0,
              }}
            >
              {admin?.initials ?? '—'}
            </div>
            {!collapsed ? (
              <div style={{ overflow: 'hidden' }}>
                <div className="bbm-truncate" style={{ fontSize: 12.5, fontWeight: 700, color: '#CBD5DD' }}>
                  {admin?.name ?? roleLabel}
                </div>
                <div style={{ fontSize: 11.5, color: '#6e85a0', marginTop: 1 }}>{roleLabel}</div>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => setConfirmSignOut(true)}
            title="Se déconnecter"
            style={{
              width: '100%',
              padding: collapsed ? '8px 0' : '8px 12px',
              border: '1px solid rgba(51,80,107,0.8)',
              background: 'transparent',
              color: '#7a8ea0',
              borderRadius: 9,
              fontSize: 12.5,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 14 }}>⏻</span>
            {!collapsed ? <span>Se déconnecter</span> : null}
          </button>
        </div>
      </nav>

      {/* ── Main ── */}
      <div className="bbm-main">
        <header className="bbm-topbar">
          <div className="bbm-topbar__left">
            <button
              type="button"
              className="bbm-hamburger"
              onClick={() => setMobileOpen(true)}
              aria-label="Ouvrir le menu"
            >
              ☰
            </button>
            <div className="bbm-topbar__accent" aria-hidden="true" />
            <div style={{ minWidth: 0 }}>
              <div className="bbm-truncate bbm-topbar__title">{title}</div>
              {subtitle ? <div className="bbm-topbar__sub">{subtitle}</div> : null}
            </div>
          </div>

          <div className="bbm-topbar__user">
            <div className="bbm-topbar__user-text">
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#4a5560', whiteSpace: 'nowrap' }}>
                {admin?.name ?? '—'}
              </div>
              <div style={{ fontSize: 11.5, color: '#8A95A3' }}>{roleLabel}</div>
            </div>
            <div
              title={admin?.name ?? ''}
              style={{
                width: 34, height: 34, borderRadius: 99,
                background: '#7FB3D5', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 13, flexShrink: 0,
              }}
            >
              {admin?.initials ?? '—'}
            </div>
          </div>
        </header>

        <main className="bbm-content">
          <Outlet context={{ snapshot, refreshBadges: snapshot.reload }} />
        </main>
      </div>

      {confirmSignOut ? (
        <ConfirmModal
          title="Se déconnecter ?"
          body="Vous allez être redirigé vers la page de connexion."
          confirmLabel="Se déconnecter"
          danger
          onConfirm={() => { setConfirmSignOut(false); handleSignOut(); }}
          onCancel={() => setConfirmSignOut(false)}
        />
      ) : null}
    </div>
  );
}
